import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { DIRECTION_CONFIG, LM_TOKENS, SENTIMENT_CONFIG } from '../../live-monitor/config';
import type { LiveCall } from '../../live-monitor/types';
import { fmtDur, useTickingDuration } from '../../live-monitor/utils';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';

export interface LiveMonitorCallDrawerProps {
  call: LiveCall;
  onClose: () => void;
}

export function LiveMonitorCallDrawer({ call, onClose }: LiveMonitorCallDrawerProps) {
  const dur = useTickingDuration(call.duration);
  const dirCfg = DIRECTION_CONFIG[call.direction];
  const sentCfg = SENTIMENT_CONFIG[call.sentiment];

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose]);

  const initials = call.name
    .split(' ')
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

  return createPortal(
    <>
      <div
        role="presentation"
        className="lm-scrim fixed inset-0 z-[var(--z-overlay)]"
        style={{
          background: 'rgba(28, 24, 18, 0.18)',
          backdropFilter: 'blur(2px)',
          WebkitBackdropFilter: 'blur(2px)',
        }}
        onClick={onClose}
      />
      <aside
        className="lm-drawer fixed bottom-0 right-0 top-0 z-[calc(var(--z-overlay)+1)] flex flex-col overflow-hidden border-l border-[var(--cream-2)] bg-[var(--cream-1)]"
        style={{ width: 560, boxShadow: LM_TOKENS.shadowDrawer }}
      >
        <header className="relative border-b border-[var(--cream-2)] px-6 pb-[18px] pt-[22px]">
          <div
            className="absolute left-6 right-6 top-0 h-px opacity-40"
            style={{ background: LM_TOKENS.iriLinear }}
          />
          <div className="flex items-start gap-[13px]">
            <div
              className="inline-flex h-12 w-12 flex-none items-center justify-center rounded-full text-base font-semibold"
              style={{
                background: LM_TOKENS.brassTint,
                color: LM_TOKENS.brassDeep,
                border: '1px solid rgba(168, 132, 92, 0.18)',
              }}
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="m-0 text-[22px] font-semibold leading-[1.15] tracking-[-0.03em] text-[var(--ink-0)]">
                {call.name}
              </h2>
              <div className="lm-mono mb-2.5 mt-1 text-[11.5px] font-medium text-[var(--ink-2)]">
                {call.phone} · {call.location}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <span
                  className="rounded-full px-[9px] py-0.5 text-[11.5px] font-semibold tracking-[-0.005em]"
                  style={{
                    background: `${dirCfg.color}1A`,
                    color: dirCfg.color,
                  }}
                >
                  {dirCfg.label}
                </span>
                <span className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-[var(--ink-2)]">
                  <span
                    className="rounded-full"
                    style={{ width: 8, height: 8, background: sentCfg.color }}
                  />
                  {sentCfg.label}
                </span>
              </div>
            </div>
            <IconButton
              icon="x"
              size={32}
              onClick={onClose}
              label="Close"
              className="flex-none rounded-[9px] border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]"
            />
          </div>

          <div className="mt-3.5 flex flex-wrap items-center gap-[7px] text-[12px] font-medium text-[var(--ink-2)]">
            <Icon kind="clock" size={12} color={LM_TOKENS.ink4} stroke={2} />
            <span>On call for</span>
            <span className="lm-mono font-bold text-[var(--ink-0)]">{fmtDur(dur)}</span>
            <span style={{ color: LM_TOKENS.ink5 }}>·</span>
            <span>{call.goal}</span>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              className="inline-flex h-9 cursor-pointer items-center justify-center gap-[7px] rounded-[9px] border px-4 text-[13px] font-semibold tracking-[-0.005em] text-white"
              style={{
                borderColor: LM_TOKENS.greenDeep,
                background: LM_TOKENS.green,
                boxShadow: LM_TOKENS.shadowXs,
              }}
            >
              <Icon kind="activity" size={12} stroke={2} color="#fff" />
              Listen in
            </button>
            <button
              type="button"
              className="inline-flex h-9 cursor-pointer items-center justify-center gap-[7px] rounded-[9px] border border-[rgba(28,24,18,0.15)] bg-[var(--cream-1)] px-3.5 text-[13px] font-medium tracking-[-0.005em] text-[var(--ink-0)] shadow-[var(--sh-1)]"
            >
              <Icon kind="team" size={12} stroke={2} />
              Take over
            </button>
            <button
              type="button"
              className="inline-flex h-9 cursor-pointer items-center justify-center gap-[7px] rounded-[9px] border border-[rgba(28,24,18,0.15)] bg-[var(--cream-1)] px-3.5 text-[13px] font-medium tracking-[-0.005em] text-[var(--ink-0)] shadow-[var(--sh-1)]"
            >
              <Icon kind="chat" size={12} stroke={2} />
              View transcript
            </button>
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-5 overflow-auto px-6 py-5">
          <div
            className="rounded-xl px-[18px] py-4"
            style={{
              background: LM_TOKENS.greenWash,
              border: `1px solid ${LM_TOKENS.greenSoft}`,
            }}
          >
            <div
              className="lm-eyebrow mb-2 text-[9.5px]"
              style={{ color: LM_TOKENS.greenDeep }}
            >
              Summary
            </div>
            <p className="m-0 text-[14px] font-medium leading-[1.6] tracking-[-0.005em] text-[var(--ink-0)]">
              {call.summary || call.utterance}
            </p>
          </div>

          <div>
            <div className="lm-eyebrow mb-3">Action timeline</div>
            <div className="overflow-hidden rounded-xl border border-[var(--cream-2)] bg-[var(--cream-1)]">
              {call.actions.map((action, i) => {
                const isRunning = action.state === 'running';
                const isLast = i === call.actions.length - 1;
                return (
                  <div
                    key={`${action.label}-${action.t}`}
                    className="grid items-start gap-3.5 px-4 py-[13px]"
                    style={{
                      gridTemplateColumns: 'auto 1fr auto',
                      borderBottom: isLast ? 'none' : '1px solid var(--cream-2)',
                      background: isRunning ? LM_TOKENS.greenWash : 'transparent',
                    }}
                  >
                    <div
                      className="mt-px inline-flex h-[26px] w-[26px] flex-none items-center justify-center rounded-lg"
                      style={{
                        background: isRunning ? LM_TOKENS.green : 'var(--surface-mute)',
                        color: isRunning ? '#fff' : 'var(--ink-2)',
                      }}
                    >
                      {isRunning ? (
                        <span
                          className="lm-breathe rounded-full bg-white"
                          style={{ width: 6, height: 6 }}
                        />
                      ) : (
                        <Icon kind="check-circ" size={12} stroke={2} />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13px] font-semibold tracking-[-0.005em] text-[var(--ink-0)]">
                        {action.label}
                      </div>
                      {action.sub && (
                        <div className="mt-0.5 text-[12px] tracking-[-0.005em] text-[var(--ink-2)]">
                          {action.sub}
                        </div>
                      )}
                    </div>
                    <span className="lm-mono mt-1 flex-none text-[10.5px] font-medium text-[var(--ink-2)]">
                      {fmtDur(action.t)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {call.facts && call.facts.length > 0 && (
            <div>
              <div className="lm-eyebrow mb-3">What Elva knows</div>
              <div className="overflow-hidden rounded-xl border border-[var(--cream-2)] bg-[var(--cream-1)]">
                {call.facts.map((fact, i) => (
                  <div
                    key={fact.k}
                    className="grid items-baseline gap-3.5 px-3.5 py-[11px]"
                    style={{
                      gridTemplateColumns: 'minmax(110px, auto) 1fr',
                      borderBottom: i < call.facts!.length - 1 ? '1px solid var(--cream-2)' : 'none',
                    }}
                  >
                    <div className="text-[11.5px] font-semibold tracking-[-0.005em] text-[var(--ink-2)]">
                      {fact.k}
                    </div>
                    <div className="text-[13px] font-medium tracking-[-0.005em] text-[var(--ink-0)]">
                      {fact.v}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </aside>
    </>,
    document.body,
  );
}
